import { toast } from "sonner";
import { FileDown, RefreshCw, Loader2, Sparkles, FileText, ShieldCheck } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import JobProgress from "@/components/JobProgress";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function Section({ title, children }) {
  return (
    <div className="mt-6">
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-zinc-500 border-b border-zinc-300 pb-1">
        {title}
      </h3>
      <div className="mt-2 text-[15px] leading-relaxed text-zinc-800">{children}</div>
    </div>
  );
}

function Prose({ text, k }) {
  return String(text || "").split("\n").map((p, i) => p.trim() && <p key={`${k}-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>);
}

function Bullets({ items, k }) {
  return <ul className="list-disc pl-5 space-y-1">{(items || []).map((p, i) => <li key={`${k}-${i}-${String(p).slice(0, 24)}`}>{p}</li>)}</ul>;
}

function SubBlock({ title, children, testId }) {
  return (
    <div data-testid={testId} className="mt-3">
      <h4 className="text-[14px] font-semibold text-zinc-700">{title}</h4>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DynamicSections({ c, t }) {
  const rd = c.ratio_decidendi;
  return (
    <>
      {rd && (
        <div data-testid="report-ratio-decidendi"><Section title={t("report.ratioDecidendi")}>
          {rd.kaidah_hukum && <SubBlock testId="rd-rule" title={t("report.rdRule")}><Prose text={rd.kaidah_hukum} k="rdk" /></SubBlock>}
          {rd.fakta_material?.length > 0 && <SubBlock testId="rd-facts" title={t("report.rdFacts")}><Bullets items={rd.fakta_material} k="rdf" /></SubBlock>}
          {rd.obiter_dicta?.length > 0 && <SubBlock testId="rd-obiter" title={t("report.rdObiter")}><Bullets items={rd.obiter_dicta} k="rdo" /></SubBlock>}
          {rd.batas_penerapan && <SubBlock testId="rd-limits" title={t("report.rdLimits")}><Prose text={rd.batas_penerapan} k="rdb" /></SubBlock>}
          {rd.nilai_otoritas && <SubBlock testId="rd-authority" title={t("report.rdAuthority")}><Prose text={rd.nilai_otoritas} k="rdn" /></SubBlock>}
        </Section></div>
      )}
      {c.ratio_legis && <div data-testid="report-ratio-legis"><Section title={t("report.ratioLegis")}><Prose text={c.ratio_legis} k="rl" /></Section></div>}
      {c.lingkup_berlaku && <div data-testid="report-scope"><Section title={t("report.scope")}><Prose text={c.lingkup_berlaku} k="lb" /></Section></div>}
      {c.norma_inti?.length > 0 && <div data-testid="report-core-norms"><Section title={t("report.coreNorms")}><Bullets items={c.norma_inti} k="ni" /></Section></div>}
    </>
  );
}

export default function ReportPreview({ open, onOpenChange, report, generating, phase, onGenerate, caseId }) {
  const { t, lang } = useI18n();
  const c = report?.content;

  const exportFile = async (format) => {
    try {
      const res = await api.get(`/cases/${caseId}/report/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("report.downloaded", { format: format.toUpperCase() }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0c] border-zinc-800 text-zinc-100 w-[calc(100vw-1.5rem)] max-w-5xl max-h-[92vh] p-0 overflow-hidden">
        <DialogTitle className="sr-only">{t("report.title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("report.title")}</DialogDescription>
        <div className="flex items-center justify-between gap-2 flex-wrap px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-800 bg-[#121216]">
          <div className="hidden sm:flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#d4af37]" />
            <span className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              {t("report.title")}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <LanguageToggle />
            <Button data-testid="regen-report-btn" onClick={onGenerate} disabled={generating}
              variant="ghost" className="h-9 text-zinc-400 hover:text-zinc-100 px-2 sm:px-3">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">{report ? t("report.regen") : t("report.compose")}</span>
            </Button>
            <Button data-testid="export-pdf-btn" onClick={() => exportFile("pdf")} disabled={!report}
              className="h-9 bg-zinc-50 text-zinc-950 hover:bg-white rounded-full px-4">
              <FileDown className="h-4 w-4 mr-1.5" /> PDF
            </Button>
            <Button data-testid="export-docx-btn" onClick={() => exportFile("docx")} disabled={!report}
              variant="outline" className="h-9 border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-full px-4">
              <FileDown className="h-4 w-4 mr-1.5" /> DOCX
            </Button>
          </div>
        </div>

        <div className="overflow-y-auto p-3 sm:p-6 md:p-10" style={{ maxHeight: "calc(92vh - 64px)" }}>
          {generating ? (
            <div className="grid place-items-center py-24 text-zinc-500">
              <Loader2 className="h-7 w-7 animate-spin text-[#d4af37]" />
              <p className="mt-4 font-mono-ui text-sm">{t("report.generating")}</p>
              <div className="mt-6"><JobProgress kind="report" phase={phase} dark /></div>
              <p className="mt-6 max-w-sm text-center text-[12px] text-zinc-500">{t("job.hint")}</p>
            </div>
          ) : !report ? (
            <div className="grid place-items-center py-24 text-center">
              <Sparkles className="h-8 w-8 text-zinc-700" strokeWidth={1.5} />
              <p className="mt-4 text-zinc-400 max-w-md">
                {t("report.empty")}
              </p>
            </div>
          ) : (
            <div
              data-testid="report-paper"
              className="mx-auto max-w-3xl bg-[#faf9f6] text-zinc-900 rounded-sm shadow-2xl p-6 sm:p-10 md:p-14"
            >
              <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {t("report.title")}
              </p>
              <h1 className="mt-3 font-serif-display text-2xl sm:text-3xl md:text-4xl font-normal leading-tight text-zinc-900">
                {c.judul || t("report.title")}
              </h1>
              <p className="mt-2 font-mono-ui text-[11px] text-zinc-500">
                Lexiqa · {new Date(report.generated_at).toLocaleDateString(lang === "en" ? "en-US" : "id-ID", { day: "numeric", month: "long", year: "numeric" })}
              </p>
              {c.jenis_dokumen && (
                <span data-testid="report-doc-type-badge" className="mt-3 inline-block rounded-full border border-[#d4af37]/60 bg-[#d4af37]/10 px-3 py-1 font-mono-ui text-[10px] uppercase tracking-[0.15em] text-[#8a6d12]">
                  {t("report.docType")}: {t(`report.type_${c.jenis_dokumen}`)}
                </span>
              )}

              {c.pendahuluan_fakta ? (
                <>
                  {(c.sitasi && Object.values(c.sitasi).some(Boolean)) && (
                    <div className="mt-2 rounded-md border border-zinc-300 bg-zinc-50 p-4 -mt-4 mb-2">
                      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-zinc-500 border-b border-zinc-300 pb-1">
                        {t("report.sitasi")}
                      </h3>
                      <dl className="mt-2 space-y-1 text-[15px] text-zinc-800">
                        {c.sitasi.nama_kasus && <div className="flex gap-2"><dt className="font-medium w-40 shrink-0">{t("report.caseName")}</dt><dd>{c.sitasi.nama_kasus}</dd></div>}
                        {c.sitasi.nomor_perkara && <div className="flex gap-2"><dt className="font-medium w-40 shrink-0">{t("report.caseNumber")}</dt><dd>{c.sitasi.nomor_perkara}</dd></div>}
                        {c.sitasi.pengadilan && <div className="flex gap-2"><dt className="font-medium w-40 shrink-0">{t("report.court")}</dt><dd>{c.sitasi.pengadilan}</dd></div>}
                        {c.sitasi.tanggal && <div className="flex gap-2"><dt className="font-medium w-40 shrink-0">{t("report.date")}</dt><dd>{c.sitasi.tanggal}</dd></div>}
                        {c.sitasi.majelis_hakim && <div className="flex gap-2"><dt className="font-medium w-40 shrink-0">{t("report.judges")}</dt><dd>{c.sitasi.majelis_hakim}</dd></div>}
                      </dl>
                    </div>
                  )}

                  <Section title={t("report.facts")}>
                    {String(c.pendahuluan_fakta).split("\n").map((p, i) => p.trim() && <p key={`fak-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                  </Section>

                  {c.isu_hukum?.length > 0 && (
                    <Section title={t("report.issues")}>
                      <ol className="list-decimal pl-5 space-y-1">{c.isu_hukum.map((p, i) => <li key={`isu-${i}-${String(p).slice(0, 24)}`}>{p}</li>)}</ol>
                    </Section>
                  )}

                  {c.argumen_para_pihak && (
                    <Section title={t("report.args")}>
                      {String(c.argumen_para_pihak).split("\n").map((p, i) => p.trim() && <p key={`arg-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                    </Section>
                  )}

                  {c.penalaran_analisis && (
                    <Section title={t("report.reasoning")}>
                      {String(c.penalaran_analisis).split("\n").map((p, i) => p.trim() && <p key={`pen-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                    </Section>
                  )}

                  {c.putusan_ratio && (
                    <Section title={t("report.holding")}>
                      {String(c.putusan_ratio).split("\n").map((p, i) => p.trim() && <p key={`put-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                    </Section>
                  )}

                  {c.analisis_kritis && (
                    <Section title={t("report.critique")}>
                      {String(c.analisis_kritis).split("\n").map((p, i) => p.trim() && <p key={`kri-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                    </Section>
                  )}

                  {c.kesimpulan && (
                    <Section title={t("report.conclusion")}>
                      {String(c.kesimpulan).split("\n").map((p, i) => p.trim() && <p key={`kes-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                    </Section>
                  )}

                  <DynamicSections c={c} t={t} />
                </>
              ) : (
                <>
              <Section title={t("report.summary")}>
                {String(c.ringkasan_perkara || "").split("\n").map((p, i) => p.trim() && <p key={`ring-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
              </Section>

              {c.para_pihak?.length > 0 && (
                <Section title={t("report.parties")}>
                  <ul className="list-disc pl-5 space-y-1">{c.para_pihak.map((p, i) => <li key={`pihak-${i}-${String(p).slice(0, 24)}`}>{p}</li>)}</ul>
                </Section>
              )}

              {c.kronologi?.length > 0 && (
                <Section title={t("report.chronology")}>
                  <div className="space-y-2">
                    {c.kronologi.map((k, i) => (
                      <div key={`kron-${i}-${(k.tanggal || "") + String(k.peristiwa).slice(0, 16)}`} className="flex gap-3">
                        <span className="font-mono-ui text-[13px] text-zinc-500 shrink-0 w-28">{k.tanggal || "—"}</span>
                        <span>{k.peristiwa}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {c.isu_hukum?.length > 0 && (
                <Section title={t("report.issues")}>
                  <ol className="list-decimal pl-5 space-y-1">{c.isu_hukum.map((p, i) => <li key={`isu-${i}-${String(p).slice(0, 24)}`}>{p}</li>)}</ol>
                </Section>
              )}

              {c.analisis && (
                <Section title={t("report.analysis")}>
                  {String(c.analisis).split("\n").map((p, i) => p.trim() && <p key={`an-${i}-${p.slice(0, 24)}`} className="mb-2">{p}</p>)}
                </Section>
              )}

              {c.penilaian_risiko && (
                <Section title={t("report.risk")}><p>{c.penilaian_risiko}</p></Section>
              )}

              {c.rekomendasi_strategi?.length > 0 && (
                <Section title={t("report.reco")}>
                  <ol className="list-decimal pl-5 space-y-1">{c.rekomendasi_strategi.map((p, i) => <li key={`rek-${i}-${String(p).slice(0, 24)}`}>{p}</li>)}</ol>
                </Section>
              )}
                </>
              )}

              <p className="mt-10 pt-4 border-t border-zinc-300 text-[11px] leading-relaxed text-zinc-500">
                {t("report.disclaimer")}
              </p>
              {report.documents_purged && (
                <p data-testid="purge-notice" className="mt-3 flex items-center gap-1.5 text-[11px] leading-relaxed text-emerald-700">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> {t("report.purged")}
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
